import { useState, useEffect, useCallback, useRef } from "react";



export const useDebouncedSearch = (
  callback?: (value: string) => void,
  delay: number = 500,
) => {
  const [searchValue, setSearchValue] = useState<string>("");
  const [debouncedValue, setDebouncedValue] = useState<string>("");
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const callbackRef = useRef(callback);

  // Update callback ref when it changes
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // Debounce effect
  useEffect(() => {
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // When search is cleared: call API without search filter immediately (matches GenericFilter onReset / clear + Enter)
    if (searchValue.length === 0) {
      setDebouncedValue("");
      if (callbackRef.current) {
        callbackRef.current("");
      }
      return;
    }

    // When search has value: debounce then call callback
    timeoutRef.current = setTimeout(() => {
      setDebouncedValue(searchValue);
      if (callbackRef.current) {
        callbackRef.current(searchValue);
      }
    }, delay);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [searchValue, delay]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Memoized setter function
  const handleSearchChange = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  return {
    searchValue,
    setSearchValue: handleSearchChange,
    debouncedValue,
    // Helper to reset search
    resetSearch: useCallback(() => {
      setSearchValue("");
      setDebouncedValue("");
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    }, []),
  };
};
